import { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useOutletContext } from "react-router-dom";
import { useSnackbar } from "notistack";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import SidePanel from "components/SidePanel";
import ConfirmDialog from "components/ConfirmDialog";
import usePaginatedResource from "hooks/usePaginatedResource";
import useMediaUpload from "hooks/useMediaUpload";
import { listMediaAssets, updateMediaAsset, deleteMediaAsset } from "api/endpoints/media";

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

// Inline metadata editor rendered inside the SidePanel.
function MediaEditForm({ websiteId, asset, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({
    altText: asset.alt_text || "",
    caption: asset.caption || "",
    description: asset.description || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await updateMediaAsset(websiteId, asset.media_asset_id, form);
      enqueueSnackbar("Asset updated", { variant: "success" });
      onSaved();
    } catch {
      enqueueSnackbar("Failed to update asset", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack component="form" spacing={3} onSubmit={handleSubmit} noValidate>
      {asset.mime_type?.startsWith("image/") && asset.file_url && (
        <Box
          component="img"
          src={asset.file_url}
          alt={asset.alt_text || ""}
          sx={{ width: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 1 }}
        />
      )}
      <MDTypography variant="button" color="text">
        {asset.original_filename}
      </MDTypography>
      <TextField label="Alt text" value={form.altText} onChange={set("altText")} fullWidth />
      <TextField label="Caption" value={form.caption} onChange={set("caption")} fullWidth />
      <TextField
        label="Description"
        value={form.description}
        onChange={set("description")}
        fullWidth
        multiline
        minRows={3}
      />
      <MDButton type="submit" color="info" variant="gradient" disabled={saving} fullWidth>
        {saving ? "Saving…" : "Save Changes"}
      </MDButton>
    </Stack>
  );
}

MediaEditForm.propTypes = {
  websiteId: PropTypes.string.isRequired,
  asset: PropTypes.shape({
    media_asset_id: PropTypes.string,
    original_filename: PropTypes.string,
    file_url: PropTypes.string,
    mime_type: PropTypes.string,
    alt_text: PropTypes.string,
    caption: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onSaved: PropTypes.func.isRequired,
};

// Batch 3.4 — Media library. Overwrites the Batch-1 placeholder.
function MediaLibraryPage() {
  const { websiteId } = useOutletContext();
  const { enqueueSnackbar } = useSnackbar();
  const { upload, status: uploadStatus } = useMediaUpload({ websiteId });
  const fileInputRef = useRef(null);

  const fetchAssets = useCallback(
    (pageParams) => {
      if (!websiteId) return { items: [], total: 0 };
      return listMediaAssets(websiteId, pageParams);
    },
    [websiteId]
  );

  const { rows, total, loading, page, pageSize, setPage, refresh } =
    usePaginatedResource(fetchAssets);

  const [editAsset, setEditAsset] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    const altText = window.prompt("Alt text (accessibility & SEO):", "") || "";
    try {
      await upload(file, { altText });
      enqueueSnackbar("Asset uploaded", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Upload failed", { variant: "error" });
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteMediaAsset(websiteId, confirmDeleteId);
      enqueueSnackbar("Asset deleted", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to delete asset", { variant: "error" });
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDTypography variant="h5" fontWeight="bold">
          Media Library
        </MDTypography>
        <input ref={fileInputRef} type="file" hidden onChange={handleUpload} />
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Icon>upload</Icon>}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadStatus === "uploading"}
        >
          {uploadStatus === "uploading" ? "Uploading…" : "Upload"}
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          {loading && (
            <MDBox display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </MDBox>
          )}

          {!loading && rows.length === 0 && (
            <MDBox textAlign="center" py={6}>
              <Icon sx={{ fontSize: "3rem !important", color: "text.secondary" }}>perm_media</Icon>
              <MDTypography variant="button" color="text" display="block" mt={1}>
                No media yet — upload your first asset.
              </MDTypography>
            </MDBox>
          )}

          {!loading && rows.length > 0 && (
            <Grid container spacing={2}>
              {rows.map((asset) => {
                const isImage = asset.mime_type?.startsWith("image/");
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={asset.media_asset_id}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <Box
                        sx={{
                          height: 150,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.100",
                          overflow: "hidden",
                        }}
                      >
                        {isImage && asset.file_url ? (
                          <Box
                            component="img"
                            src={asset.file_url}
                            alt={asset.alt_text || ""}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <Icon sx={{ fontSize: "3rem !important", color: "text.secondary" }}>
                            description
                          </Icon>
                        )}
                      </Box>
                      <MDBox p={1.5}>
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          sx={{ display: "block", wordBreak: "break-word" }}
                        >
                          {asset.original_filename}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {asset.mime_type || "—"}
                          {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ""}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {formatBytes(asset.file_size)}
                          {asset.visibility ? ` · ${asset.visibility}` : ""}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{ display: "block", fontStyle: "italic", minHeight: 18 }}
                        >
                          {asset.alt_text || "No alt text"}
                        </MDTypography>
                        <MDBox display="flex" justifyContent="flex-end" gap={0.5} mt={0.5}>
                          <MDButton
                            size="small"
                            variant="text"
                            color="info"
                            onClick={() => setEditAsset(asset)}
                          >
                            Edit
                          </MDButton>
                          <MDButton
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() => setConfirmDeleteId(asset.media_asset_id)}
                          >
                            Delete
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {!loading && total > pageSize && (
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                disabled={page <= 0}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </MDButton>
              <MDTypography variant="caption" color="text">
                Page {page + 1} of {lastPage + 1} · {total} assets
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                disabled={page >= lastPage}
                onClick={() => setPage(page + 1)}
              >
                Next
              </MDButton>
            </MDBox>
          )}
        </MDBox>
      </Card>

      <SidePanel
        open={Boolean(editAsset)}
        onClose={() => setEditAsset(null)}
        title="Edit Asset"
        subtitle="Update the asset's accessibility and caption metadata"
        width={760}
      >
        {editAsset && (
          <MediaEditForm
            websiteId={websiteId}
            asset={editAsset}
            onSaved={() => {
              setEditAsset(null);
              refresh();
            }}
          />
        )}
      </SidePanel>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete Asset"
        message="Are you sure you want to delete this media asset? This cannot be undone."
        confirmText="Delete"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteId(null)}
      />
    </MDBox>
  );
}

export default MediaLibraryPage;
